import { AppsService, AppsServiceModule } from '@b2b/common/services/apps/apps.service';
import { CartService, CartServiceModule } from '@b2b/common/services';
import type { PendoService } from '@b2b/common/services/pendo';
import { RedemptionService, RedemptionServiceModule } from '@b2b/common/services/redemption/redemption.service';
import { ACMEValidator, ValidateService } from '@b2b/common/services/validate';
import { getGiftCardRedemptionsTotal } from '@b2b/common/utils/gift-card';
import { withGiftCardMask } from '@b2b/common/utils/masks';
import { GiftCardRedemption } from '@b2b/types/cart';
import { extractACMEErrorType, TODO } from '@b2b/types/core';
import angular, { IOnChangesObject, IRootScopeService } from 'angular';
import { v4 as uuidv4 } from 'uuid';

const APPLY_ERROR_FIELD = 'gift-card-number';
const APPLY_ERROR_MESSAGE = "We couldn't apply this gift card. Please check the card number and PIN.";
const NO_BALANCE_ERROR_TYPE = 'NO_BALANCE_LEFT';
const NO_BALANCE_ERROR_MESSAGE = 'This gift card balance is $0. Please try another payment method.';
const APPLY_GIFT_CARD_EVENT = 'Apply Gift Card - Clicked';

class GiftCardRedemptionController {
  static $inject = [
    '$rootScope',
    'RedemptionService',
    'CartService',
    'AppsService',
    'ValidateService',
    'ThemesHelperService',
    'PendoService',
  ];

  /** binding: the amount still owed on the order, used as the redemption amount. */
  amountDue = 0;

  /**
   * binding: when true, this component tracks applied redemptions itself
   * instead of reading/writing `CartService` — used by screens (org order
   * checkout/payment) that have no shopping cart to key redemptions off of.
   */
  standalone = false;

  /**
   * binding: set by the host screen when checkout/payment failed because a gift card's
   * hold expired. acme core has already dropped the dangling redemption server-side by the
   * time this arrives, so this only needs to make the UI catch up with that.
   */
  authorizationExpired = false;

  /** binding: reservation id to associate with the redemption (new-order/checkout flows). */
  reservationId?: string;

  /** binding: order id to associate with the redemption (existing-order flows with no reservation). */
  orderId?: string;

  /**
   * binding: redemptions already linked to the order/reservation before this
   * component mounted (e.g. pending stored-value holds from a prior attempt)
   * — seeded into standalone mode's local list so they show as applied and
   * count toward the amount due.
   */
  initialRedemptions?: GiftCardRedemption[];

  /** binding: callback invoked whenever the applied redemptions change, in standalone mode. */
  onRedemptionsChange: (locals: { redemptions: GiftCardRedemption[]; total: number }) => void = () => undefined;

  /** Redemptions applied so far, tracked locally. Only used in standalone mode. */
  redemptions: GiftCardRedemption[] = [];

  // The redemption inputs are visible by default so users can redeem a gift
  // card immediately; after one is applied the form collapses and the
  // "add another gift card" link re-opens it for additional cards.
  showGiftCardForm = true;
  isApplyingGiftCard = false;
  giftCardErrors: Record<string, TODO> = {};
  giftCardIdempotencyKey?: string;
  giftCardMask = withGiftCardMask();
  giftCardNumber = '';
  form = { giftCardPin: '' };

  constructor(
    private $rootScope: IRootScopeService,
    private redemptionService: RedemptionService,
    private cartService: CartService,
    private appsService: AppsService,
    private validateService: ValidateService,
    private themesHelperService: TODO,
    private pendoService: PendoService,
  ) {}

  $onInit() {
    this.appsService.ensureLoaded();
    if (this.standalone) {
      if (this.initialRedemptions?.length) {
        this.redemptions = this.initialRedemptions;
        this.showGiftCardForm = false;
        // Recompute the total from this canonical list rather than trusting
        // the host's own pre-computed total to still match it.
        this.emitRedemptionsChange();
      }
    } else {
      this.cartService.getCart().finally(() => {
        if (this.getGiftCardRedemptions().length > 0) {
          this.showGiftCardForm = false;
        }
      });
    }
  }

  $onChanges(changes: IOnChangesObject) {
    if (changes.authorizationExpired?.currentValue) {
      this.handleAuthorizationExpired();
    }
  }

  /**
   * The checkout/payment error that reports this doesn't say which applied card expired, and
   * acme core has already dropped the dangling redemption server-side by this point - so the
   * UI just needs to catch up with that. In cart-backed mode, refetching the cart picks up
   * whatever redemptions are actually still there. In standalone mode (org order screens,
   * which have no cart to refetch), there's nothing to query, so every locally-tracked
   * redemption is cleared and the customer can re-apply a card if still needed.
   */
  private handleAuthorizationExpired(): void {
    if (this.standalone) {
      this.redemptions = [];
      this.emitRedemptionsChange();
    } else {
      this.refreshCart();
    }
  }

  /** Section only renders when the tenant can redeem stored value (gift cards). */
  get giftCardAppInstalled(): boolean {
    return this.appsService.canRedeemStoredValue();
  }

  getGiftCardRedemptions(): GiftCardRedemption[] {
    return this.standalone ? this.redemptions : this.cartService.getModel().giftCardRedemptions || [];
  }

  maskGiftCardNumber(lastFour: string): string {
    const value = lastFour || '';
    return `•••• ${value.slice(-4)}`;
  }

  showGiftCardEntry(): void {
    this.giftCardNumber = '';
    this.form.giftCardPin = '';
    this.giftCardMask = withGiftCardMask();
    this.giftCardErrors = {};
    this.giftCardIdempotencyKey = undefined;
    this.showGiftCardForm = true;
  }

  validateGiftCard(): boolean {
    const cardNumber = this.giftCardMask.value.unmasked;
    const validators: ACMEValidator[] = [
      {
        field: 'gift-card-number',
        len: { message: 'Enter a valid gift card number', test: { value: cardNumber, minLength: 1 } },
      },
      {
        field: 'gift-card-pin',
        len: { message: 'Enter the gift card PIN', test: { value: this.form.giftCardPin, minLength: 1 } },
      },
    ];

    const valid = this.validateService.validate(validators);
    this.giftCardErrors = valid ? {} : this.validateService.getErrorHash();

    return valid;
  }

  applyGiftCard(): void {
    if (this.isApplyingGiftCard || !this.validateGiftCard()) {
      return;
    }

    this.isApplyingGiftCard = true;
    // Reuse the same key across retries of a failed redemption; regenerate after success.
    this.giftCardIdempotencyKey = this.giftCardIdempotencyKey || uuidv4();

    this.redemptionService.api
      .redeem({
        tenantId: this.getTenantId(),
        cardNumber: this.giftCardMask.value.unmasked,
        pin: this.form.giftCardPin,
        amount: String(this.amountDue),
        cartId: this.standalone ? undefined : this.cartService.getModel().id,
        reservationId: this.reservationId,
        orderId: this.orderId,
        idempotencyKey: this.giftCardIdempotencyKey,
      })
      .then((response) => {
        this.pendoService.track(APPLY_GIFT_CARD_EVENT, { result: 'success' });
        this.showGiftCardForm = false;
        this.giftCardErrors = {};
        this.giftCardIdempotencyKey = undefined;
        if (this.standalone) {
          this.addLocalRedemption(response);
        } else {
          this.refreshCart();
        }
      })
      .catch((error) => {
        this.pendoService.track(APPLY_GIFT_CARD_EVENT, {
          result: 'failure',
          errorType: extractACMEErrorType(error?.data) || null,
        });
        this.giftCardErrors = this.getGiftCardError(error);
      })
      .finally(() => {
        this.isApplyingGiftCard = false;
      });
  }

  removeGiftCard(redemption: GiftCardRedemption): void {
    if (this.isApplyingGiftCard) {
      return;
    }

    this.isApplyingGiftCard = true;

    this.redemptionService.api
      .remove({
        tenantId: this.getTenantId(),
        authorizationId: redemption.authorizationId,
        cartId: this.standalone ? undefined : this.cartService.getModel().id,
        reservationId: this.reservationId,
        orderId: this.orderId,
      })
      .then(() => {
        if (this.standalone) {
          this.removeLocalRedemption(redemption.authorizationId);
        } else {
          this.refreshCart();
        }
      })
      .catch((error) => {
        this.giftCardErrors = this.getGiftCardError(error);
      })
      .finally(() => {
        this.isApplyingGiftCard = false;
      });
  }

  /** Standalone mode: append the redemption returned by `redeem()` and notify the host screen. */
  private addLocalRedemption(response: TODO): void {
    const redemption = response?.data?.payload as GiftCardRedemption | undefined;
    if (redemption) {
      this.redemptions = [...this.redemptions, redemption];
    }
    this.emitRedemptionsChange();
  }

  /** Standalone mode: drop a redemption after a successful `remove()` and notify the host screen. */
  private removeLocalRedemption(authorizationId: string): void {
    this.redemptions = this.redemptions.filter((redemption) => redemption.authorizationId !== authorizationId);
    this.emitRedemptionsChange();
  }

  private emitRedemptionsChange(): void {
    if (this.getGiftCardRedemptions().length === 0) {
      this.showGiftCardEntry();
    }
    this.onRedemptionsChange({
      redemptions: this.getGiftCardRedemptions(),
      total: getGiftCardRedemptionsTotal(this.getGiftCardRedemptions()),
    });
  }

  getGiftCardError(error?: TODO): Record<string, TODO> {
    let errorMessage = APPLY_ERROR_MESSAGE;

    const errorType = extractACMEErrorType(error?.data);

    if (errorType === NO_BALANCE_ERROR_TYPE) {
      errorMessage = NO_BALANCE_ERROR_MESSAGE;
    }

    this.validateService.validate([
      {
        field: APPLY_ERROR_FIELD,
        len: { message: errorMessage, test: { value: '', minLength: 1 } },
      },
    ]);

    return this.validateService.getErrorHash();
  }

  /** Reloads the cart (to pick up giftCardRedemptions) then notifies the payment flow. */
  refreshCart(): void {
    this.cartService.getCart().finally(() => {
      // When all gift cards have been removed, return to the initial state:
      // a blank entry form (cleared id/pin) visible again.
      if (this.getGiftCardRedemptions().length === 0) {
        this.showGiftCardEntry();
      }
      this.$rootScope.$broadcast('GiftCard::Updated');
    });
  }

  private getTenantId(): string {
    return String(this.themesHelperService.getCompany('tenantId') || '');
  }
}

export const GiftCardRedemptionComponentModule = angular
  .module('acme.common.component.gift-card-redemption', [
    AppsServiceModule,
    CartServiceModule,
    RedemptionServiceModule,
    'acme.common.service.validate',
    'acme.common.service.themesHelper',
  ])
  .component('acmeGiftCardRedemption', {
    templateUrl: 'common/components/gift-card-redemption/gift-card-redemption.component.tpl.html',
    controller: GiftCardRedemptionController,
    controllerAs: '$ctrl',
    bindings: {
      amountDue: '<',
      standalone: '<',
      authorizationExpired: '<',
      reservationId: '<?',
      orderId: '<?',
      initialRedemptions: '<?',
      onRedemptionsChange: '&',
    },
  }).name;

export { GiftCardRedemptionController };
